import type { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { environment } from "../environments/environment";
import { ErrorHandler, inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";

type TelemetryCall = (appInsights: ApplicationInsights) => void;

@Injectable()
export class MonitoringService {
    private router = inject(Router);

    private appInsights?: ApplicationInsights;

    private sdkUnavailable = false;

    private callsAwaitingSdk: TelemetryCall[] = [];

    constructor() {
        this.loadSdk().catch(() => {
            this.sdkUnavailable = true;
            this.callsAwaitingSdk = [];
        });
    }

    logPageView(name?: string, url?: string) {
        this.send(appInsights => appInsights.trackPageView({ name: name, uri: url }));
    }

    logEvent(name: string, properties?: { [key: string]: any }) {
        this.send(appInsights => appInsights.trackEvent({ name: name }, properties));
    }

    logMetric(name: string, average: number, properties?: { [key: string]: any }) {
        this.send(appInsights => appInsights.trackMetric({ name: name, average: average }, properties));
    }

    logException(exception: Error, severityLevel?: number) {
        this.send(appInsights => appInsights.trackException({ exception: exception, severityLevel: severityLevel }));
    }

    logTrace(message: string, properties?: { [key: string]: any }) {
        this.send(appInsights => appInsights.trackTrace({ message: message }, properties));
    }

    private send(call: TelemetryCall): void {
        if (this.appInsights) {
            call(this.appInsights);
            return;
        }

        if (this.sdkUnavailable) {
            return;
        }

        this.callsAwaitingSdk.push(call);
    }

    private async loadSdk(): Promise<void> {
        const [{ ApplicationInsights }, { AngularPlugin }] = await Promise.all([
            import("@microsoft/applicationinsights-web"),
            import("@microsoft/applicationinsights-angularplugin-js"),
        ]);

        const angularPlugin = new AngularPlugin();
        const appInsights = new ApplicationInsights({
            config: {
                instrumentationKey: environment.instrumentationKey,
                extensions: [angularPlugin],
                extensionConfig: {
                    [angularPlugin.identifier]: {
                        router: this.router,
                        errorServices: [new ErrorHandler()],
                    },
                },
            },
        });
        appInsights.loadAppInsights();
        this.appInsights = appInsights;

        const queued = this.callsAwaitingSdk;
        this.callsAwaitingSdk = [];
        queued.forEach(call => call(appInsights));
    }
}
